import { page } from '$app/stores';
import { get } from 'svelte/store';
import { capture_supabase_error } from '$lib/utils/capture_supabase_error';

const video_percentage_complete = 0.95;

/**
 * Save answer to localStorage for unlogged users
 * @param {object} options
 * @param {string} options.product_id
 * @param {number} options.lesson_id
 * @param {string} options.question_id
 * @param {string} options.question_text
 * @param {string} options.user_answer
 * @param {boolean} options.is_correct
 * @param {string=} options.correct_answer
 */
export function save_temp_answer({
    product_id,
    lesson_id,
    question_id,
    question_text,
    user_answer,
    is_correct,
    correct_answer = null
}) {
    const key = `critikid_answers_${product_id}_${lesson_id}`;
    const answers = JSON.parse(localStorage.getItem(key) || '{}');
    answers[question_id] = {
        question_text,
        user_answer,
        correct_answer,
        is_correct
    };
    localStorage.setItem(key, JSON.stringify(answers));
}

/**
 * Get temporary answers from localStorage for a specific lesson
 * @param {string} product_id
 * @param {number} lesson_id
 * @returns {object} answers object with question_id as keys
 */
export function get_temp_answers(product_id, lesson_id) {
    const key = `critikid_answers_${product_id}_${lesson_id}`;
    return JSON.parse(localStorage.getItem(key) || '{}');
}

/**
 * Clear temporary answers from localStorage for a specific lesson
 * @param {string} product_id
 * @param {number} lesson_id
 */
export function clear_temp_answers(product_id, lesson_id) {
    const key = `critikid_answers_${product_id}_${lesson_id}`;
    localStorage.removeItem(key);
}

/**
 * Get all temporary answers for a course (all lessons)
 * @param {string} product_id
 * @returns {object} all answers grouped by lesson_id
 */
export function get_all_temp_answers_for_product(product_id) {
    const answers = {};
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`critikid_answers_${product_id}_`)) {
            const lesson_id = key.replace(`critikid_answers_${product_id}_`, '');
            answers[lesson_id] = JSON.parse(localStorage.getItem(key) || '{}');
        }
    }
    return answers;
}

/**
 * Clear all temporary answers for a course
 * @param {string} product_id
 */
export function clear_all_temp_answers_for_product(product_id) {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`critikid_answers_${product_id}_`)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Atomically delete a specific temporary answer to prevent race conditions
 * @param {string} product_id
 * @param {number|string} lesson_id
 * @param {number|string} question_id
 */
export function delete_temp_answer_atomic(product_id, lesson_id, question_id) {
    const key = `critikid_answers_${product_id}_${lesson_id}`;
    
    try {
        // Single atomic operation: read, modify, write
        const current_answers = JSON.parse(localStorage.getItem(key) || '{}');
        delete current_answers[question_id];
        
        // Either remove the key entirely or update with remaining answers
        if (Object.keys(current_answers).length === 0) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, JSON.stringify(current_answers));
        }
    } catch (error) {
        console.error('Error atomically deleting temp answer:', error);
    }
}

/**
 * @param {object} options
 * @param {string} options.video_id
 * @param {string} options.product_id
 * @param {number=} options.duration
 * @param {number=} options.current_time
 * @param {boolean|undefined=} options.complete
 * @returns {Promise<void>}
 */
export async function save_video_progress({
    video_id,
    product_id,
    duration = 0,
    current_time = 0,
    complete
}) {
    const { supabase, session } = get(page).data;

    if (!session) return;

    const { error } = await supabase.from('User_Video_Progress').upsert(
        {
            user_id: session.user.id,
            video_id: video_id,
            product_id: product_id,
            progress: current_time ?? duration ?? 0,
            complete:
                complete ?? current_time >= duration * video_percentage_complete
        },
        {
                onConflict: 'user_id,product_id,video_id',
            returning: 'minimal'
        }
    );

    if (error) {
        console.error(error);
        capture_supabase_error(error, {
            message: 'Video progress upsert failed',
            operation: 'upsert',
            table: 'User_Video_Progress'
        });
    }
}

/**
 * @param {object} options
 * @param {string} options.product_id
 * @param {number} options.lesson_id
 * @param {string} options.question_id
 * @param {string} options.question_text
 * @param {string} options.user_answer
 * @param {boolean} options.is_correct
 * @param {string=} options.correct_answer
 * @param {import('@supabase/supabase-js').SupabaseClient=} options.supabase_client
 * @param {import('@supabase/supabase-js').Session=} options.user_session
 * @returns {Promise<{success: boolean, stored: 'db' | 'local', error?: any}>}
 */
export async function save_question_answer({
    product_id,
    lesson_id,
    question_id,
    question_text,
    user_answer,
    correct_answer = null,
    is_correct,
    supabase_client,
    user_session
}) {
    /** @type {{supabase?: import('@supabase/supabase-js').SupabaseClient, session?: import('@supabase/supabase-js').Session}} */
    const page_data = get(page)?.data || {};
    const active_supabase = supabase_client || page_data.supabase;
    const active_session = user_session || page_data.session;

    if (active_session && active_supabase) {
        const { error } = await active_supabase.from('User_Answers2').upsert(
            {
                user_id: active_session.user.id,
                product_id,
                lesson_id,
                question_id,
                question_text,
                correct_answer,
                user_answer,
                is_correct
            },
            {
                onConflict: 'lesson_id,user_id,product_id,question_id'
            }
        );

        if (error) {
            console.error(error);
            capture_supabase_error(error, {
                message: 'Question answer upsert failed',
                operation: 'upsert',
                table: 'User_Answers2'
            });
            return { success: false, stored: 'db', error };
        }

        return { success: true, stored: 'db' };
    }

    save_temp_answer({
        product_id,
        lesson_id,
        question_id,
        question_text,
        user_answer,
        correct_answer,
        is_correct
    });

    return { success: true, stored: 'local' };
}

/**
 * @param {object} options
 * @param {string} options.classroom_lesson_id
 * @param {string} options.student_session_id
 * @param {string} options.question_id
 * @param {string=} options.question_text
 * @param {QuestionType=} options.question_type
 * @param {string=} options.correct_answer
 * @param {string=} options.user_answer
 * @param {boolean=} options.is_correct
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase_client
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function save_classroom_answer({
    classroom_lesson_id,
    student_session_id,
    question_id,
    question_text = null,
    question_type = null,
    correct_answer = null,
    user_answer = null,
    is_correct = null,
    supabase_client
}) {
    if (!supabase_client) {
        return { success: false, error: new Error('Missing Supabase client') };
    }

    if (!classroom_lesson_id || !student_session_id || !question_id) {
        return {
            success: false,
            error: new Error('Missing classroom answer identifiers')
        };
    }

    const { error } = await supabase_client.rpc('student_session_upsert_answer', {
        p_classroom_lesson_id: classroom_lesson_id,
        p_student_session_id: student_session_id,
        p_question_id: question_id,
        p_question_text: question_text,
        p_question_type: question_type,
        p_correct_answer: correct_answer,
        p_user_answer: user_answer,
        p_is_correct: is_correct
    });

    if (error) {
        console.error('save_classroom_answer_error', error);
        capture_supabase_error(error, {
            message: 'Classroom answer RPC failed',
            operation: 'rpc',
            rpc: 'student_session_upsert_answer'
        });
        return { success: false, error };
    }

    return { success: true };
}

/**
 * @param {object} options
 * @param {string} options.classroom_lesson_id
 * @param {string} options.student_session_id
 * @param {string} options.video_id
 * @param {number=} options.duration
 * @param {number=} options.current_time
 * @param {boolean|undefined=} options.complete
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase_client
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function save_classroom_video_progress({
    classroom_lesson_id,
    student_session_id,
    video_id,
    duration = 0,
    current_time = 0,
    complete,
    supabase_client
}) {
    if (!supabase_client) {
        return { success: false, error: new Error('Missing Supabase client') };
    }

    if (!classroom_lesson_id || !student_session_id || !video_id) {
        return {
            success: false,
            error: new Error('Missing classroom video progress identifiers')
        };
    }

    const { error } = await supabase_client.rpc(
        'student_session_upsert_video_progress',
        {
            p_classroom_lesson_id: classroom_lesson_id,
            p_student_session_id: student_session_id,
            p_video_id: video_id,
            p_progress: current_time ?? duration ?? 0,
            p_complete:
                complete ?? current_time >= duration * video_percentage_complete
        }
    );

    if (error) {
        console.error('save_classroom_video_progress_error', error);
        capture_supabase_error(error, {
            message: 'Classroom video progress RPC failed',
            operation: 'rpc',
            rpc: 'student_session_upsert_video_progress'
        });
        return { success: false, error };
    }

    return { success: true };
}

/**
 * @param {object} options
 * @param {string} options.classroom_lesson_id
 * @param {string} options.student_session_id
 * @param {string=} options.completed_at
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase_client
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function save_classroom_lesson_progress({
    classroom_lesson_id,
    student_session_id,
    completed_at = new Date().toISOString(),
    supabase_client
}) {
    if (!supabase_client) {
        return { success: false, error: new Error('Missing Supabase client') };
    }

    if (!classroom_lesson_id || !student_session_id) {
        return {
            success: false,
            error: new Error('Missing classroom lesson progress identifiers')
        };
    }

    const { error } = await supabase_client.rpc(
        'student_session_upsert_lesson_progress',
        {
            p_classroom_lesson_id: classroom_lesson_id,
            p_student_session_id: student_session_id,
            p_completed_at: completed_at || null
        }
    );

    if (error) {
        console.error('save_classroom_lesson_progress_error', error);
        capture_supabase_error(error, {
            message: 'Classroom lesson progress RPC failed',
            operation: 'rpc',
            rpc: 'student_session_upsert_lesson_progress'
        });
        return { success: false, error };
    }

    return { success: true };
}

/**
 * Clear all answers for a lesson — from DB if logged in, from localStorage otherwise.
 * Always wipes the localStorage entry for the lesson regardless of auth.
 *
 * @param {object} options
 * @param {string} options.product_id
 * @param {number} options.lesson_id
 * @param {import('@supabase/supabase-js').SupabaseClient=} options.supabase_client
 * @param {import('@supabase/supabase-js').Session=} options.user_session
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function clear_lesson_answers({
    product_id,
    lesson_id,
    supabase_client,
    user_session
}) {
    /** @type {{supabase?: import('@supabase/supabase-js').SupabaseClient, session?: import('@supabase/supabase-js').Session}} */
    const page_data = get(page)?.data || {};
    const active_supabase = supabase_client || page_data.supabase;
    const active_session = user_session || page_data.session;

    if (typeof window !== 'undefined' && localStorage) {
        clear_temp_answers(product_id, lesson_id);
    }

    if (active_session && active_supabase) {
        const { error } = await active_supabase
            .from('User_Answers2')
            .delete()
            .eq('user_id', active_session.user.id)
            .eq('product_id', product_id)
            .eq('lesson_id', lesson_id);

        if (error) {
            console.error(error);
            capture_supabase_error(error, {
                message: 'Clear lesson answers failed',
                operation: 'delete',
                table: 'User_Answers2'
            });
            return { success: false, error };
        }
    }

    return { success: true };
}

/**
 * Migrate temporary answers from localStorage to database.
 * Awaits all upserts; deletes localStorage entry on success.
 * Use the `_background` variant when you don't want to block.
 *
 * @param {object} options
 * @param {string} options.product_id
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase_client
 * @param {import('@supabase/supabase-js').Session} options.user_session
 * @returns {Promise<void>}
 */
export async function migrate_temp_answers_to_db({
    product_id,
    supabase_client,
    user_session
}) {
    if (!product_id || !supabase_client || !user_session) return;
    if (typeof window === 'undefined' || !localStorage) return;

    const all_temp_answers = get_all_temp_answers_for_product(product_id);
    /** @type {Promise<any>[]} */
    const tasks = [];

    for (const [lesson_id, answers] of Object.entries(all_temp_answers)) {
        for (const [question_id, answer] of Object.entries(answers)) {
            tasks.push(
                save_question_answer({
                    product_id,
                    lesson_id: parseInt(lesson_id),
                    question_id,
                    question_text: answer.question_text,
                    user_answer: answer.user_answer,
                    is_correct: answer.is_correct,
                    correct_answer: answer.correct_answer,
                    supabase_client,
                    user_session
                })
                    .then((result) => {
                        if (result?.success && result.stored === 'db') {
                            delete_temp_answer_atomic(
                                product_id,
                                lesson_id,
                                question_id
                            );
                        }
                    })
                    .catch((error) => {
                        console.error('Error migrating temp answer:', error);
                    })
            );
        }
    }

    await Promise.all(tasks);
}

/**
 * Fire-and-forget variant of migrate_temp_answers_to_db.
 * @param {object} options
 * @param {string} options.product_id
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase_client
 * @param {import('@supabase/supabase-js').Session} options.user_session
 */
export function migrate_temp_answers_to_db_background(options) {
    migrate_temp_answers_to_db(options);
}
